import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CheckCircle2, Loader2, Gauge, Camera, Upload, AlertCircle, Sun, ArrowRight,
} from 'lucide-react';

const API = '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';
const MPAN_GUIDE_IMG = 'https://customer-assets-jt897jd0.emergentagent.net/job_317c24ef-1b68-48d8-b64e-56fdbe5ba447/artifacts/a8svq7ox_MPAN-number-example.jpg';
const CUTOUT_GUIDE_IMG = 'https://customer-assets-jt897jd0.emergentagent.net/job_317c24ef-1b68-48d8-b64e-56fdbe5ba447/artifacts/2t7kwq2z_Electric%20Meter%20Cupboard%20example.jpeg';

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(', ') || fallback;
  return fallback;
};

const looksLikeMpan = (v) => {
  const digits = (v || '').replace(/\D/g, '');
  return digits.length === 13 || digits.length === 21;
};

const PublicDnoRequest = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);

  const [mpanInput, setMpanInput] = useState('');
  const [mpanSaving, setMpanSaving] = useState(false);
  const [mpanScanning, setMpanScanning] = useState(false);
  const [mpanScanNote, setMpanScanNote] = useState('');
  const [cutoutUploading, setCutoutUploading] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/public/dno-request/${token}`);
      setData(res.data);
      if (res.data.mpan) setMpanInput(res.data.mpan);
    } catch (e) {
      setFatal(errText(e, 'This request link is invalid or has expired.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submitMpan = async () => {
    const mpan = (mpanInput || '').trim();
    if (!mpan) return;
    if (!looksLikeMpan(mpan) && !window.confirm("That doesn't look like a typical MPAN (usually 13 or 21 digits, starting with an S box). Save it anyway?")) return;
    setMpanSaving(true); setError('');
    try {
      await axios.post(`${API}/public/dno-request/${token}/mpan`, { mpan });
      setData((s) => ({ ...s, has_mpan: true, mpan }));
    } catch (e) {
      setError(errText(e, 'Could not save your MPAN. Please try again.'));
    } finally {
      setMpanSaving(false);
    }
  };

  const scanBillForMpan = async (file) => {
    if (!file) return;
    setMpanScanning(true); setError(''); setMpanScanNote('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: r } = await axios.post(`${API}/public/dno-request/${token}/mpan-ocr`, fd);
      const bits = [];
      if (r?.mpan) { setMpanInput(r.mpan); bits.push('MPAN filled in below'); }
      if (r?.annual_usage_kwh || r?.monthly_usage_kwh) bits.push('usage saved');
      if (r?.bill_saved) bits.push('bill saved to your file');
      if (bits.length) {
        setMpanScanNote(`Bill scanned — ${bits.join(', ')}.${r?.mpan ? ' Please double-check the MPAN below.' : ''}`);
        if (!r?.mpan) setError("We couldn't read the MPAN from that photo — please type it in below.");
      } else {
        setError("We couldn't read the MPAN from that photo — please type it in below.");
      }
    } catch {
      setError("We couldn't read the MPAN from that photo — please type it in below.");
    } finally {
      setMpanScanning(false);
    }
  };

  const uploadCutout = async (file) => {
    if (!file) return;
    setCutoutUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/public/dno-request/${token}/cutout`, fd);
      setData((s) => ({ ...s, has_cutout: true }));
    } catch (e) {
      setError(errText(e, 'Could not upload your photo. Please try again.'));
    } finally {
      setCutoutUploading(false);
    }
  };

  // Ordered list of the items this customer was asked for.
  const steps = ['mpan', 'cutout'].filter((i) => (data?.items || []).includes(i));
  const isDone = (i) => (i === 'mpan' ? data?.has_mpan : data?.has_cutout);
  const allDone = steps.length > 0 && steps.every(isDone);
  const multi = steps.length > 1;

  // Auto-advance to the next outstanding step once the current one is done.
  useEffect(() => {
    if (!data || steps.length === 0) return;
    if (isDone(steps[step])) {
      const next = steps.findIndex((s, idx) => idx > step && !isDone(s));
      if (next !== -1) setStep(next);
    }
    // eslint-disable-next-line
  }, [data?.has_mpan, data?.has_cutout]);

  const current = steps[step];

  const renderMpan = () => (
    data?.has_mpan ? (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-testid="mpan-done">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <div className="text-sm text-emerald-800">MPAN saved <span className="font-mono">{data.mpan}</span></div>
      </div>
    ) : (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="mpan-card">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-blue-600" />
          <p className="text-sm font-semibold text-slate-900">Your MPAN (electricity supply number)</p>
        </div>
        <p className="text-xs text-slate-500">
          You can do this in one of two ways — whichever is easier:
        </p>

        {/* Option 1 — scan the bill */}
        <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 space-y-2">
          <p className="text-xs font-semibold text-blue-800">Option 1 — Upload your electricity bill</p>
          <p className="text-[11px] text-slate-500">Snap a photo of any recent bill and we'll read the MPAN for you automatically.</p>
          <img src={MPAN_GUIDE_IMG} alt="Where to find your MPAN on an electricity bill" className="w-full rounded border border-slate-200" data-testid="mpan-guide-img" />
          <label className="block">
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => scanBillForMpan(e.target.files?.[0])} data-testid="mpan-scan-input" />
            <span className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium cursor-pointer border ${mpanScanning ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}>
              {mpanScanning ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading your bill…</> : <><Camera className="w-4 h-4" /> Upload / photograph my bill</>}
            </span>
          </label>
          {mpanScanNote && (
            <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="mpan-scan-note">
              <CheckCircle2 className="w-3.5 h-3.5" /> {mpanScanNote}
            </p>
          )}
        </div>

        {/* Option 2 — type it */}
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <label htmlFor="mpan-field" className="block text-xs font-semibold text-slate-800">
            Option 2 — Type your MPAN here
          </label>
          <p className="text-[11px] text-slate-500">
            It's the long number on your bill next to the "S" box (13 or 21 digits).
          </p>
          <Input
            id="mpan-field"
            value={mpanInput}
            onChange={(e) => setMpanInput(e.target.value)}
            placeholder="e.g. 20 0001 2345 678"
            className="font-mono text-base"
            inputMode="numeric"
            data-testid="mpan-input"
          />
          <Button onClick={submitMpan} disabled={mpanSaving || !mpanInput.trim()} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="mpan-save-btn">
            {mpanSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Save my MPAN
          </Button>
        </div>
      </div>
    )
  );

  const renderCutout = () => (
    data?.has_cutout ? (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-testid="cutout-done">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <div className="text-sm text-emerald-800">Meter cupboard photo received — thank you!</div>
      </div>
    ) : (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2" data-testid="cutout-card">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-blue-600" />
          <p className="text-sm font-semibold text-slate-900">Photo of your meter cupboard (cut-out fuse)</p>
        </div>
        <p className="text-xs text-slate-500">
          Please take a clear photo of your electricity meter cupboard showing the main cut-out fuse — like the example below.
        </p>
        <img src={CUTOUT_GUIDE_IMG} alt="Example photo of a meter cupboard / cut-out fuse" className="w-full rounded border border-slate-200 max-h-56 object-cover" data-testid="cutout-guide-img" />
        <label className="block">
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadCutout(e.target.files?.[0])} data-testid="cutout-file-input" />
          <span className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium cursor-pointer ${cutoutUploading ? 'bg-slate-300 text-slate-600' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
            {cutoutUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Take / upload photo</>}
          </span>
        </label>
      </div>
    )
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-700 via-blue-600 to-sky-500 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-center mb-6">
          <img src={LOGO_URL} alt="Helios" className="h-12" data-testid="dno-request-logo" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
            </div>
          ) : fatal ? (
            <div className="text-center py-10" data-testid="dno-request-fatal">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <p className="text-slate-700 font-medium">{fatal}</p>
              <p className="text-sm text-slate-500 mt-2">Please contact us and we'll send you a fresh link.</p>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h1 className="text-xl font-bold text-slate-900" data-testid="dno-request-title">
                  Hi {data.first_name || 'there'} 👋
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                  To progress your solar installation with the network operator (DNO), we just need the details below.
                </p>
              </div>

              {allDone ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3" data-testid="dno-request-complete">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div className="text-sm text-emerald-800 font-medium">
                    All done — thank you! We've got everything we need.
                  </div>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700" data-testid="dno-request-error">
                      {error}
                    </div>
                  )}

                  {/* Step indicator — only when both are requested */}
                  {multi && (
                    <div className="flex items-center justify-center gap-2" data-testid="dno-request-steps">
                      {steps.map((s, i) => (
                        <div key={s} className="flex items-center gap-2">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                            isDone(s) ? 'bg-emerald-500 text-white'
                              : i === step ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                          }`}>
                            {isDone(s) ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                          </span>
                          <span className={`text-xs ${i === step ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
                            {s === 'mpan' ? 'MPAN' : 'Cut-out photo'}
                          </span>
                          {i < steps.length - 1 && <span className="text-slate-300">›</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {multi && (
                    <p className="text-center text-xs text-slate-400" data-testid="dno-request-step-count">
                      Step {step + 1} of {steps.length}
                    </p>
                  )}

                  {/* Only the current step's card */}
                  {current === 'mpan' ? renderMpan() : renderCutout()}

                  {/* Manual advance (e.g. skip to the other one for now) */}
                  {multi && step < steps.length - 1 && (
                    <div className="flex justify-end">
                      <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => setStep(step + 1)} data-testid="dno-request-next-btn">
                        Do the next one <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </div>
                  )}
                </>
              )}

              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-2">Prefer to log in? You can also upload these from your customer portal:</p>
                <Button asChild variant="outline" className="w-full border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="dno-request-portal-btn">
                  <Link to="/portal/login"><Sun className="w-4 h-4 mr-2" /> Go to my portal</Link>
                </Button>
              </div>
            </>
          )}
        </div>
        <p className="text-center text-xs text-white/70 mt-4">Helios — your details are kept secure.</p>
      </div>
    </div>
  );
};

export default PublicDnoRequest;
